import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import WhatsAppPopup from './WhatsAppPopup';

const { FiCheck, FiCalendar, FiUsers, FiGift, FiMail, FiClock } = FiIcons;

const ThankYouPage = () => {
  const handleWhatsAppJoin = () => {
    window.open('https://chat.whatsapp.com/E8bqQ0e70W8HQyRAPtKde8', '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const bonusCourses = [
    { name: 'Crorepati Business Model™', value: '₹19,997' },
    { name: 'Crorepati Codex™', value: '₹14,997' },
    { name: 'Automation Code™', value: '₹14,997' },
    { name: 'Webinar Mastery Codex™', value: '₹14,997' },
    { name: 'Scale With AI Ads™', value: '₹14,997' },
    { name: 'Scale With Metrics Codex™', value: '₹14,997' }
  ];

  const workshopDays = [
    { day: 'Day 1', title: 'High-Ticket Funnels + Leads', value: '₹24,997' },
    { day: 'Day 2', title: 'Closing High-Ticket 1-to-1', value: '₹49,997' },
    { day: 'Day 3', title: 'One-to-Many Scaling', value: '₹49,997' }
  ];

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 selection:bg-emerald-100"
    >
      <WhatsAppPopup />

      {/* Header Section */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
        <motion.div
          variants={itemVariants}
          className="relative w-24 h-24 mx-auto mb-8"
        >
          {/* Pulsing Background Rings */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 bg-emerald-400 rounded-full"
          />
          <motion.div
            animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
            className="absolute inset-0 bg-blue-400 rounded-full"
          />
          
          {/* Animated Tick Container */}
          <motion.div
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20,
              delay: 0.4 
            }}
            className="relative z-10 w-full h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg"
          >
            <motion.svg
              viewBox="0 0 24 24"
              fill="none"
              className="w-12 h-12 text-white stroke-current"
              strokeWidth="3"
            >
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                d="M5 13l4 4L19 7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </motion.div>
        </motion.div>

        <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
          Payment Successful! 🎉
        </motion.h1>

        <motion.p variants={itemVariants} className="text-xl text-gray-600 mb-8">
          Welcome to the <span className="font-bold text-emerald-600">High-Ticket Workshop</span>
        </motion.p>

        <motion.div
          variants={itemVariants}
          className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] p-10 border border-gray-100 relative overflow-hidden group"
        >
          <div className="absolute top-0 left-0 w-2 h-full bg-emerald-500" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Your Journey to ₹10L+ Monthly Revenue Starts Now
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            You've secured your seat for the 3-Day High-Ticket Workshop + Lifetime Access to 6 Premium Courses.
          </p>
        </motion.div>
      </div>

      {/* WhatsApp Community CTA - Animated Banner */}
      <motion.div variants={itemVariants} className="max-w-4xl mx-auto px-6 mb-16">
        <motion.div
          whileHover={{ y: -5 }}
          className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl p-10 text-white text-center shadow-xl relative overflow-hidden"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl"
          />
          <SafeIcon icon={FiUsers} className="text-5xl mx-auto mb-6 relative z-10" />
          <h3 className="text-3xl font-bold mb-4 relative z-10">Join Our Private Community</h3>
          <p className="text-lg mb-8 opacity-90 relative z-10 max-w-xl mx-auto">
            Connect with high-performers, get instant updates, and access bonus materials before we go live.
          </p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)" }}
            whileTap={{ scale: 0.95 }}
            onClick={handleWhatsAppJoin}
            className="bg-white text-emerald-600 px-10 py-5 rounded-2xl font-black text-xl shadow-lg transition-all duration-300 relative z-10"
          >
            Join WhatsApp Group →
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Workshop Overview - Grid with Hover Effects */}
      <div className="max-w-6xl mx-auto px-6 mb-20">
        <motion.div variants={itemVariants} className="text-center mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-4">The 3-Day Framework</h2>
          <p className="text-xl text-gray-500">Three Systems. One New Revenue Ceiling.</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {workshopDays.map((workshop, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              className="bg-white rounded-3xl p-8 shadow-lg border border-gray-50 hover:border-emerald-100 transition-all duration-300"
            >
              <div className="inline-block px-4 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-black uppercase mb-4 tracking-wider">
                {workshop.day}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 h-14">
                {workshop.title}
              </h3>
              <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                <span className="text-gray-400 text-sm">Value</span>
                <span className="font-bold text-emerald-600">{workshop.value}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bonus Area - Dark Themed for Premium Feel */}
      <motion.div variants={itemVariants} className="max-w-6xl mx-auto px-6 mb-20">
        <div className="bg-gray-900 rounded-[3rem] p-12 text-white border border-gray-800 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <SafeIcon icon={FiGift} className="text-[12rem]" />
          </div>
          
          <div className="relative z-10">
            <div className="text-center mb-12">
              <span className="bg-emerald-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase mb-4 inline-block">Lifetime Access</span>
              <h2 className="text-4xl font-black mb-4">₹93,982+ Worth of Bonuses</h2>
              <p className="text-gray-400 text-lg">Your toolkit for absolute market dominance.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bonusCourses.map((course, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.05)" }}
                  className="p-6 rounded-2xl border border-gray-800 bg-gray-800/50 backdrop-blur-sm"
                >
                  <h4 className="font-bold text-white mb-2">{course.name}</h4>
                  <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm">
                    <SafeIcon icon={FiCheck} /> {course.value}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Next Steps - Modern List */}
      <div className="max-w-4xl mx-auto px-6 mb-24">
        <motion.div variants={itemVariants} className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100">
          <h2 className="text-3xl font-black text-gray-900 mb-10 text-center">Your Next 10 Minutes</h2>
          
          <div className="space-y-8">
            {[
              { icon: FiMail, title: "Check Your Email", desc: "Detailed access links and credentials are on their way.", color: "blue" },
              { icon: FiUsers, title: "Join Community", desc: "Introduce yourself in the WhatsApp group and meet your peers.", color: "green" },
              { icon: FiCalendar, title: "Block Your Calendar", desc: "Ensure you're available for the live implementation sessions.", color: "purple" }
            ].map((step, i) => (
              <motion.div key={i} className="flex items-center gap-6 group">
                <div className={`w-14 h-14 rounded-2xl bg-${step.color}-50 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={step.icon} className={`text-2xl text-${step.color}-600`} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{step.title}</h3>
                  <p className="text-gray-500">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="text-center pb-20 px-6">
        <motion.p variants={itemVariants} className="text-gray-400 text-sm max-w-md mx-auto">
          Secure Payment Confirmation. © 2024 High-Ticket Workshop. All rights reserved.
        </motion.p>
      </footer>
    </motion.div>
  );
};

export default ThankYouPage;